const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
const STATIC_EXTRA_SOURCE = 'buyereviews_search_extras.json';
const baseCache = new Map();
let staticExtrasPromise = null;

const normalizeText = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const normalizeUrl = (value = '') => {
  if (!value) return '';

  try {
    const parsed = new URL(value, window.location.origin);
    parsed.hash = '';
    return `${parsed.pathname}${parsed.search}`;
  } catch (_error) {
    return normalizeText(value);
  }
};

const slugify = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildUrl = (base, params = {}) => {
  if (!base) return '';

  const url = new URL(base, window.location.origin);
  Object.keys(params).forEach((key) => {
    if (params[key]) {
      url.searchParams.set(key, params[key]);
    }
  });

  return url.toString();
};

const normalizeSearchItem = (item = {}, fallback = {}) => {
  const title = String(item.title ?? fallback.title ?? '').trim();
  if (!title) return null;

  const category = String(item.category ?? fallback.category ?? '').trim();
  const keywords = String(item.keywords ?? fallback.keywords ?? '').trim();
  const url = String(item.url ?? fallback.url ?? '').trim();
  const image = String(item.image ?? fallback.image ?? '').trim() || BLANK_IMAGE;
  const searchText = normalizeText([title, category, keywords].join(' '));

  return {
    title,
    category,
    keywords,
    url,
    image,
    searchText,
  };
};

const dedupeItems = (items = []) => {
  const seen = new Set();
  const output = [];

  items.forEach((item) => {
    const normalized = normalizeSearchItem(item);
    if (!normalized) return;

    const key = `${normalizeText(normalized.title)}|${normalizeUrl(normalized.url)}`;
    if (seen.has(key)) return;

    seen.add(key);
    output.push(normalized);
  });

  return output;
};

const flattenBuyereviewsItems = (data, listUrl) => {
  if (!Array.isArray(data)) return [];

  const items = [];

  data.forEach((category) => {
    const categoryName = category?.category_name || '';
    const listPages = Array.isArray(category?.list_page) ? category.list_page : [];

    listPages.forEach((listPage) => {
      const title = listPage?.list_page_name || '';
      if (!title) return;

      const listKeywords = [
        listPage?.list_page_data?.title,
        listPage?.list_page_data?.description,
      ]
        .filter(Boolean)
        .join(' ');

      const listLink = buildUrl(listUrl, {
        category: slugify(categoryName),
        list: slugify(title),
      });

      items.push({
        title,
        image: listPage?.list_page_image || BLANK_IMAGE,
        category: categoryName,
        keywords: listKeywords,
        url: listLink,
      });

      const products = Array.isArray(listPage?.list_page_data?.product_list)
        ? listPage.list_page_data.product_list
        : [];

      products.forEach((product) => {
        const productTitle = String(product?.product_display_name || '').trim();
        if (!productTitle) return;

        const productKeywords = [
          product?.product_description,
          product?.product_tag,
          title,
          categoryName,
        ]
          .filter(Boolean)
          .join(' ');

        items.push({
          title: productTitle,
          image: product?.product_image || BLANK_IMAGE,
          category: categoryName,
          keywords: productKeywords,
          url: buildUrl(listLink, { product: slugify(productTitle) }),
        });
      });
    });
  });

  return items;
};

const readDomExtraItems = () => {
  const scriptNodes = document.querySelectorAll('script[data-buyereviews-search-extra]');
  if (!scriptNodes.length) return [];

  const items = [];

  scriptNodes.forEach((node) => {
    const payload = node.textContent?.trim();
    if (!payload) return;

    try {
      const parsed = JSON.parse(payload);
      if (Array.isArray(parsed)) {
        items.push(...parsed);
      } else if (parsed && typeof parsed === 'object') {
        items.push(parsed);
      }
    } catch (_error) {
      // Ignore malformed payloads so search remains functional.
    }
  });

  return items;
};

const readStaticExtraItems = async () => {
  if (!staticExtrasPromise) {
    staticExtrasPromise = (async () => {
      const urls = [];

      try {
        const moduleUrl = new URL(import.meta.url);
        urls.push(new URL(STATIC_EXTRA_SOURCE, moduleUrl).toString());

        if (moduleUrl.search) {
          const versionedUrl = new URL(STATIC_EXTRA_SOURCE, moduleUrl);
          versionedUrl.search = moduleUrl.search;
          urls.push(versionedUrl.toString());
        }
      } catch (_error) {
        // Ignore invalid import URL.
      }

      try {
        urls.push(new URL(STATIC_EXTRA_SOURCE, window.location.origin).toString());
      } catch (_error) {
        // Ignore invalid origin URL.
      }

      for (const url of [...new Set(urls)]) {
        try {
          const response = await fetch(url);
          if (!response.ok) continue;
          const data = await response.json();
          if (Array.isArray(data)) return data;
        } catch (_error) {
          // Ignore failed attempts and continue to the next candidate URL.
        }
      }

      return [];
    })();
  }

  return staticExtrasPromise;
};

const readBaseItems = async ({ source, listUrl }) => {
  if (!source) return [];

  const cacheKey = `${source}|${listUrl}`;
  if (!baseCache.has(cacheKey)) {
    const promise = fetch(source)
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => flattenBuyereviewsItems(data, listUrl))
      .catch(() => []);
    baseCache.set(cacheKey, promise);
  }

  return baseCache.get(cacheKey);
};

export const getBuyereviewsSearchItems = async ({ source, listUrl = '/pages/buyereviews-detail' } = {}) => {
  const [baseItems, staticExtraItems] = await Promise.all([
    readBaseItems({ source, listUrl }),
    readStaticExtraItems(),
  ]);

  const domExtraItems = readDomExtraItems();

  // Prioritize items defined by the current page's components (DOM extras) so they surface first in search results.
  return dedupeItems([...domExtraItems, ...staticExtraItems, ...baseItems]);
};

export const filterBuyereviewsSearchItems = (items = [], query = '', limit = 8) => {
  const needle = normalizeText(query);
  if (!needle) return [];

  return items.filter((item) => normalizeText(item.searchText || '').includes(needle)).slice(0, limit);
};
